// A simple utility to check if images are cached in sessionStorage
// This is used by the home page components to determine if images need to be reloaded

/**
 * Check if the provided image URL exists in the cache
 * @param {string} imageUrl - The URL of the image to check
 * @param {string} cacheKey - The sessionStorage key where the cache is stored
 * @returns {boolean} - Whether the image is cached
 */
export const isImageCached = (imageUrl, cacheKey) => {
  if (!imageUrl || typeof window === 'undefined' || !window.sessionStorage) {
    return false;
  }
  
  try {
    const cacheString = sessionStorage.getItem(cacheKey);
    if (!cacheString) return false;
    
    const cache = JSON.parse(cacheString);
    return !!cache[imageUrl];
  } catch (error) {
    console.error('Error checking image cache:', error);
    return false;
  }
};

/**
 * Add an image URL to the cache
 * @param {string} imageUrl - The URL of the image to cache
 * @param {string} cacheKey - The sessionStorage key where the cache is stored
 */
export const addImageToCache = (imageUrl, cacheKey) => {
  if (!imageUrl || typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  
  try {
    const cacheString = sessionStorage.getItem(cacheKey) || '{}';
    const cache = JSON.parse(cacheString);
    
    cache[imageUrl] = true;
    sessionStorage.setItem(cacheKey, JSON.stringify(cache));
  } catch (error) {
    console.error('Error adding image to cache:', error);
  }
};

/**
 * Preload an image and add it to the cache if it loads successfully
 * @param {string} imageUrl - The URL of the image to preload
 * @param {string} cacheKey - The sessionStorage key where the cache is stored
 * @param {string} fallbackUrl - Optional fallback image URL if the main one fails
 * @returns {Promise<boolean>} - Whether the image loaded successfully
 */
export const preloadAndCacheImage = (imageUrl, cacheKey, fallbackUrl) => {
  return new Promise((resolve) => {
    if (!imageUrl) {
      resolve(false);
      return;
    }
    
    if (isImageCached(imageUrl, cacheKey)) {
      resolve(true);
      return;
    }
    
    const img = new Image();
    
    img.onload = () => {
      addImageToCache(imageUrl, cacheKey);
      resolve(true);
    };
    
    img.onerror = () => {
      console.warn(`Failed to load image: ${imageUrl}`);
      if (fallbackUrl) {
        const fallbackImg = new Image();
        fallbackImg.onload = () => {
          // Still mark the original as cached, but use fallback
          addImageToCache(imageUrl, cacheKey);
          resolve(true);
        };
        fallbackImg.onerror = () => {
          resolve(false);
        };
        fallbackImg.src = fallbackUrl;
      } else {
        resolve(false);
      }
    };
    
    img.src = imageUrl;
  });
};

const imageCacheUtils = {
  isImageCached,
  addImageToCache,
  preloadAndCacheImage
};

export default imageCacheUtils;
